import { Check, ChevronRight, ListChecks, Rocket } from 'lucide-react';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { FeatureFlags, BillingStatus } from '@/lib/api';

interface SetupProgressProps {
  featureFlags: FeatureFlags | null;
  billingStatus: BillingStatus | null;
  hasSentInvoice: boolean;
  className?: string;
}

interface SetupStep {
  id: string;
  label: string;
  description: string;
  completed: boolean;
  href: string;
}

export function SetupProgress({ featureFlags, billingStatus, hasSentInvoice, className }: SetupProgressProps) {
  const { nav } = useBusinessNav();

  const steps: SetupStep[] = [
    {
      id: 'invoice',
      label: 'Send your first invoice',
      description: 'Create and send an invoice to a client',
      completed: hasSentInvoice,
      href: '/invoices/create',
    },
    {
      id: 'stripe',
      label: 'Set up payments',
      description: 'Accept card and ACH payments',
      completed: featureFlags?.payments_setup_complete ?? false,
      href: '/settings/payments',
    },
    {
      id: 'subscribe',
      label: 'Choose a plan',
      description: 'Unlock all features',
      completed: billingStatus?.has_stripe_subscription ?? false,
      href: '/settings/billing',
    },
  ];

  const completedCount = steps.filter((s) => s.completed).length;
  const totalSteps = steps.length;
  const allComplete = completedCount === totalSteps;
  const progress = (completedCount / totalSteps) * 100;

  // Hide once subscribed (paid customer) or all steps complete
  if (billingStatus?.has_stripe_subscription || allComplete) {
    return null;
  }

  const remainingSteps = totalSteps - completedCount;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className={cn("group inline-flex items-center gap-2 rounded-full border bg-white shadow-sm hover:shadow hover:border-primary/20 h-9 px-4 transition-all", className)}>
          <ListChecks className="size-4 text-muted-foreground group-hover:text-foreground transition-colors" />
          <span className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors">
            Setup
          </span>
          <span className="size-5 rounded-full bg-primary text-[10px] font-medium text-white flex items-center justify-center">
            {remainingSteps}
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-80 p-0 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent px-4 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Rocket className="size-5 text-primary" />
            </div>
            <div>
              <p className="font-semibold">Get started</p>
              <p className="text-xs text-muted-foreground">
                {completedCount} of {totalSteps} steps complete
              </p>
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-3 h-1.5 bg-primary/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Steps */}
        <div className="divide-y">
          {steps.map((step) => (
            <button
              key={step.id}
              onClick={() => !step.completed && nav(step.href)}
              disabled={step.completed}
              className={cn(
                'w-full flex items-start gap-3 px-4 py-3 text-left transition-colors',
                step.completed
                  ? 'bg-muted/30'
                  : 'hover:bg-muted/50 cursor-pointer'
              )}
            >
              <div
                className={cn(
                  'mt-0.5 size-5 rounded-full flex items-center justify-center shrink-0 transition-colors',
                  step.completed
                    ? 'bg-primary text-primary-foreground'
                    : 'border-2 border-muted-foreground/30'
                )}
              >
                {step.completed && <Check className="size-3" />}
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className={cn(
                    'text-sm font-medium',
                    step.completed && 'text-muted-foreground line-through'
                  )}
                >
                  {step.label}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {step.description}
                </p>
              </div>
              {!step.completed && (
                <ChevronRight className="size-4 text-muted-foreground mt-0.5 shrink-0" />
              )}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
